import { useFormContext } from 'react-hook-form';
import { useCreateXeroEntities } from '../../../queries/use-create-xero-entities';
import { useUpdateEntitiesProvider } from '../../../queries/use-update-entities-provider';
import { OnboardingXeroEntities, OnboardingXeroEntity } from './XeroEntitiesForm';
import { XeroTenantConnection } from '../../../types/xero';
import { chunk } from '../../../utilities/chunk';
import { toastNotification } from '../../../components/toast/Toast';
import { formatErrorMessages, formatSuccessMessages, groupXeroEntityConnections } from '../utils';
import { useNavigate } from 'react-router-dom';
import { useFirmContext } from '../../../providers/FirmProvider';
import { ButtonV2, Spinner } from '@cimplico/components';
import { useState } from 'react';
import sleep from '../../../utilities/sleep';
import plural from '../../../utilities/plural';
import axios from 'axios';
import OnboardingConfirmationDialog from './OnboardingConfirmationDialog';

type EntitiesRequestError = { [entityName: string]: string[] };

type XeroEntitiesFormSubmitProps = {
    xeroUserId: string;
    tenantConnections: XeroTenantConnection[];
    onOnboardResult: (result: { successMessages?: string[]; errorMessages?: string[] }) => void;
    onCancel: () => void;
};

function XeroEntitiesFormSubmit({ xeroUserId, tenantConnections, onOnboardResult, onCancel }: XeroEntitiesFormSubmitProps): JSX.Element {
    const [progress, setProgress] = useState(0);
    const [total, setTotal] = useState(0);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const { reset, handleSubmit, watch, formState } = useFormContext<OnboardingXeroEntities>();
    const { mutateAsync: createMutateAsync } = useCreateXeroEntities({ invalidate: false });
    const { mutateAsync: updateMutateAsync } = useUpdateEntitiesProvider({ invalidate: false });
    const { shortId } = useFirmContext();

    const navigate = useNavigate();
    const connections = watch('connections');

    /**
     * Format request results into success and error
     *
     * @param results
     * @returns Number of successful creation/update, error messages for requests that failed
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleRequestError(results: any): { successCount: number; errorMessage: EntitiesRequestError } {
        let successCount = 0;
        const errorMessage: EntitiesRequestError = {};

        results.forEach((r: { statusCode: number; error: { original: { tenantId: string }; fields: { errorMessage: string } } }) => {
            if (r.statusCode < 400) {
                successCount++;
            } else if (r.statusCode >= 400) {
                const connection = tenantConnections.find((c) => c.tenantId === r.error.original.tenantId);
                if (connection) {
                    errorMessage[connection.tenantName] = [r.error.fields.errorMessage];
                }
            }
        });

        return { successCount, errorMessage };
    }

    /**
     * Create new Xero entities
     *
     * @param newEntities XeroTenantConnection[]
     * @returns Number of successful creation, error messages for creation that failed
     */
    async function createXeroEntities(newEntities: XeroTenantConnection[]): Promise<{ count: number; error: EntitiesRequestError }> {
        let count = 0;
        let error: EntitiesRequestError = {};

        if (newEntities.length === 0) {
            return { count, error };
        }

        const newEntitiesChunk = chunk(newEntities);

        for (const chunk of newEntitiesChunk) {
            try {
                const res = await createMutateAsync({
                    xeroUserId,
                    xeroEntities: chunk.map((ent) => ({ tenantId: ent.tenantId, connectionId: ent.id })),
                });
                count += res.results.length;
            } catch (res) {
                if (axios.isAxiosError(res)) {
                    const result = handleRequestError(res.response?.data.results);
                    count += result.successCount;
                    error = result.errorMessage;
                }
            }

            setProgress((prev) => prev + chunk.length);
            await sleep(1000);
        }

        return { count, error };
    }

    /**
     * Update existing entities to map to Xero
     *
     * @param newEntities OnboardingXeroEntity[]
     * @returns Number of successful update, error messages for update that failed
     */
    async function updateEntitiesProvider(
        toUpdateEntities: OnboardingXeroEntity[]
    ): Promise<{ count: number; error: EntitiesRequestError }> {
        let count = 0;
        let error: EntitiesRequestError = {};

        if (toUpdateEntities.length === 0) {
            return { count, error };
        }

        const toUpdateEntitiesChunk = chunk(toUpdateEntities);

        for (const chunk of toUpdateEntitiesChunk) {
            try {
                const res = await updateMutateAsync({
                    xeroUserId,
                    xeroEntities: chunk.map((ent) => ({
                        id: ent.entityId as string,
                        tenantId: ent.xeroEntity.tenantId,
                        connectionId: ent.xeroEntity.id,
                    })),
                });
                count += res.results.length;
            } catch (res) {
                if (axios.isAxiosError(res)) {
                    const result = handleRequestError(res.response?.data.results);
                    count += result.successCount;
                    error = result.errorMessage;
                }
            }

            setProgress((prev) => prev + chunk.length);
            await sleep(1000);
        }

        return { count, error };
    }

    async function onSubmit(): Promise<void> {
        setShowConfirmation(false);

        if (!xeroUserId) {
            return;
        }

        const { selectedEntities, newEntities, toUpdateEntities } = groupXeroEntityConnections(connections);

        onOnboardResult({});
        setProgress(0);
        setTotal(selectedEntities.length);

        const { count: entitiesCreated, error: createError } = await createXeroEntities(newEntities);
        const { count: existingEntitiesMapped, error: updateError } = await updateEntitiesProvider(toUpdateEntities);

        const errorList = { ...createError, ...updateError };

        if (Object.keys(errorList).length === 0) {
            const successMessages = formatSuccessMessages(entitiesCreated, existingEntitiesMapped);

            if (successMessages && successMessages.length > 0) {
                toastNotification(
                    'success',
                    `Successfully onboarded ${entitiesCreated + existingEntitiesMapped} Xero ${plural(
                        entitiesCreated + existingEntitiesMapped,
                        'entity',
                        'entities'
                    )}!`,
                    successMessages.join(' ')
                );
            }

            navigate(`/${shortId}/entities`);
            return;
        }

        const successMessages = formatSuccessMessages(entitiesCreated, existingEntitiesMapped);
        const errorMessages = formatErrorMessages(errorList);
        onOnboardResult({ successMessages, errorMessages });

        reset({
            connections: connections.filter(
                (ent) =>
                    Object.keys(errorList).includes(ent.xeroEntity.tenantName) ||
                    !selectedEntities.find((e) => e.xeroEntity.tenantId === ent.xeroEntity.tenantId)
            ),
        });
    }

    return (
        <div className="grid grid-cols-2 items-center mt-8">
            <OnboardingConfirmationDialog
                onClose={(): void => setShowConfirmation(false)}
                onUpdate={handleSubmit(onSubmit)}
                isOpen={showConfirmation}
            />
            <div className="flex items-center gap-4 text-sm text-gray-800">
                {formState.isSubmitting && (
                    <>
                        <p>Creating {plural(total, 'entity', 'entities')}</p>
                        <Spinner size="sm" />
                        <p className="text-gray-600">
                            {progress} / {total} ({Math.round((progress / total) * 100)}%)
                        </p>
                    </>
                )}
            </div>
            <div className="flex justify-end gap-4">
                <ButtonV2 size="sm" type="hollow" label="Cancel" onClick={onCancel} />
                <ButtonV2
                    size="sm"
                    type="primary"
                    label="Onboard"
                    isLoading={formState.isSubmitting}
                    isDisabled={
                        tenantConnections.length === 0 || connections.filter((v) => v.isSelected).length === 0 || formState.isSubmitting
                    }
                    onClick={(): void => setShowConfirmation(true)}
                />
            </div>
        </div>
    );
}

export default XeroEntitiesFormSubmit;
